import { useCallback, useEffect, useState } from "react";
import {
  DEPOSIT_CONTENT_DEFAULT_HEIGHT_RATIO,
  DEPOSIT_CONTENT_MAX_HEIGHT_RATIO,
  getBoundedDepositContentHeight,
  parseDepositHeightPayload,
} from "./deposit-height";

type UseDepositBottomSheet = {
  isDepositOpen: boolean;
  depositContentHeight: number;
  openDeposit: () => void;
  closeDeposit: () => void;
};

const getDefaultDepositContentHeight = (): number =>
  Math.round(window.innerHeight * DEPOSIT_CONTENT_DEFAULT_HEIGHT_RATIO);

/**
 * Owns the open/closed state and content height of the Header's deposit bottom sheet on web,
 * including listening for `postMessage`d height updates from the (cross-origin) deposit iframe.
 */
export const useDepositBottomSheet = (depositUrl: string | undefined): UseDepositBottomSheet => {
  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [depositContentHeight, setDepositContentHeight] = useState<number>(getDefaultDepositContentHeight);

  const setBoundedDepositContentHeight = useCallback((height: number): void => {
    const maxHeight = Math.round(window.innerHeight * DEPOSIT_CONTENT_MAX_HEIGHT_RATIO);

    setDepositContentHeight(getBoundedDepositContentHeight(height, maxHeight));
  }, []);

  const openDeposit = useCallback((): void => {
    setDepositContentHeight(getDefaultDepositContentHeight());
    setIsDepositOpen(true);
  }, []);

  const closeDeposit = useCallback((): void => {
    setIsDepositOpen(false);
  }, []);

  const onDepositMessage = useCallback(
    (event: MessageEvent): void => {
      if (!depositUrl) {
        return;
      }

      let depositAllowedOrigin: string;

      try {
        depositAllowedOrigin = new URL(depositUrl, window.location.origin).origin;
      } catch {
        return;
      }

      if (event.origin !== depositAllowedOrigin) {
        return;
      }

      const parsedHeight = parseDepositHeightPayload(event.data);

      if (parsedHeight) {
        setBoundedDepositContentHeight(parsedHeight);
      }
    },
    [setBoundedDepositContentHeight, depositUrl],
  );

  useEffect(() => {
    if (!isDepositOpen) {
      return;
    }

    window.addEventListener("message", onDepositMessage);

    return () => {
      window.removeEventListener("message", onDepositMessage);
    };
  }, [isDepositOpen, onDepositMessage]);

  return { isDepositOpen, depositContentHeight, openDeposit, closeDeposit };
};
